import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import Layout from './components/Layout/Layout';
import Home2 from './pages/Home2';
import CVUpload from './pages/CVUpload';
import CVSearch from './pages/CVSearch';
import CandidateSearch from './pages/CandidateSearch';
import Messages from './pages/Messages';
import RecruiterInbox from './pages/RecruiterInbox';
import CustomerSupport from './pages/CustomerSupport';
import AboutUs from './pages/AboutUs';
import Login from './pages/Login';
import Signup from './pages/Signup';
import RecruiterSignup from './pages/RecruiterSignup';
import SignupChoice from './pages/SignupChoice';
import AuthCallback from './pages/AuthCallback';
import EmailConfirmation from './pages/EmailConfirmation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ResendConfirmation from './pages/ResendConfirmation';
import Profile from './pages/Profile';
import RecruiterProfile from './pages/RecruiterProfile';
import BillingSettings from './pages/BillingSettings';
import WelcomeRecruiter from './pages/WelcomeRecruiter';
import Testing from './pages/Testing';
import Saved from './pages/Saved';
import { ENABLE_SAVED_PASSPORTS } from './config/features';
import CompanyManage from './pages/CompanyManage';
import CreatorManagement from './pages/admin/CreatorManagement';
import SpotlightManager from './pages/admin/SpotlightManager';
import CompanyManager from './pages/admin/CompanyManager';
import B2BHub from './pages/admin/B2BHub';
import JobWatcherHub from './pages/admin/JobWatcherHub';
import InterviewHub from './pages/admin/InterviewHub';
import InterviewReview from './pages/admin/InterviewReview';
import InviteLanding from './pages/interview/InviteLanding';
import InterviewRoom from './pages/interview/InterviewRoom';
import MockHome from './pages/mock/MockHome';
import MockHistory from './pages/mock/MockHistory';
import MockReview from './pages/mock/MockReview';
import RecruiterPreScreenReview from './pages/recruiter/RecruiterPreScreenReview';
import Spotlights from './pages/Spotlights';
import AdminGuard from './components/Auth/AdminGuard';
import RecruiterGuard from './components/Auth/RecruiterGuard';
import ProfileSync from './components/Auth/ProfileSync';
import ConsentGate from './components/Legal/ConsentGate';
import ContactOptInPrompt from './components/Legal/ContactOptInPrompt';
import './index.css';
// i18n bootstrap
import './i18n/i18n';
// Legal Pages
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import Imprint from './pages/legal/Imprint';
import Cookies from './pages/legal/Cookies';
import CreatorAgreement from './pages/legal/CreatorAgreement';
import AshbyIntegrationHelp from './pages/help/AshbyIntegration';
import Avv from './pages/legal/Avv';
// Payment Pages
import Pricing from './pages/Pricing';
import StripeCheckout from './pages/StripeCheckout';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
// Maintenance Page
import Maintenance from './pages/Maintenance';
import Careers from './pages/Careers';
import HiringBoard from './pages/HiringBoard';
import Pipeline from './pages/Pipeline';
import Applications from './pages/Applications';
import { ClipboardList } from 'lucide-react';
import ApplyPublic from './pages/ApplyPublic';
import InterestedFeed from './pages/InterestedFeed';
import InterestedPage from './pages/InterestedPage';
import PassportPreview from './pages/passport/PassportPreview';
import PassportShareLanding from './pages/PassportShareLanding';
import { captureAffiliateFromUrl } from './lib/affiliate';

const LegacyInterviewSessionRedirect: React.FC = () => {
  const { sessionId } = useParams();
  return <Navigate to={`/interviews/${sessionId}`} replace />;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}


function CommunityTalkRedirect() {
  return <Navigate to="/?tab=explore" replace />;
}

function App() {
  // Capture a partner affiliate code from ?ref= on first load (first-touch).
  // Stashed in localStorage so it survives the email-confirmation / OAuth
  // round-trip and can be attached to Supabase user_metadata on signup.
  useEffect(() => {
    captureAffiliateFromUrl();
  }, []);

  // Check if maintenance mode is enabled
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  // If maintenance mode, show maintenance page for all routes except /maintenance itself
  if (isMaintenanceMode) {
    return (
      <Router>
        <LanguageProvider>
          <Routes>
            <Route path="*" element={<Maintenance />} />
          </Routes>
        </LanguageProvider>
      </Router>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <LanguageProvider>
        <UserProfileProvider>
          <ViewModeProvider>
          <Layout>
            <ProfileSync />
            <ConsentGate />
            <ContactOptInPrompt />
            <Routes>
          <Route path="/" element={<Home2 />} />
          <Route path="/home2" element={<Home2 />} />
          <Route path="/upload" element={<CVUpload />} />
          {/* DEPRECATED: Old text-based CV search (CVSearch.tsx) - kept for backwards compat */}
          <Route path="/search" element={<CVSearch />} />
          {/* DEPRECATED: Old candidate search with JD upload + filters (CandidateSearch.tsx) - no longer linked anywhere */}
          <Route path="/candidate-search-legacy" element={<CandidateSearch />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/recruiter-inbox" element={<RecruiterInbox />} />
          <Route path="/support" element={<CustomerSupport />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/recruiter-profile" element={<RecruiterProfile />} />
          <Route path="/settings/billing" element={<BillingSettings />} />
          <Route path="/welcome-recruiter" element={<WelcomeRecruiter />} />
          <Route path="/testing" element={<Testing />} />
          {/* DEPRECATED: Creators demo (educator service deleted). Redirect old links. */}
          <Route path="/creators-demo" element={<Navigate to="/?tab=explore" replace />} />
          {/* RETIRED: Vee (vee-agent-service) and Job Radar (job-watcher-service)
              are paused. Redirect old links so they don't 404. */}
          <Route path="/vee-agent" element={<Navigate to="/?tab=explore" replace />} />
          <Route path="/saved" element={ENABLE_SAVED_PASSPORTS ? <Saved /> : <Navigate to="/?tab=explore" replace />} />
          {/* DEPRECATED: Explore → Companies. Redirect old links to Explore. */}
          <Route path="/companies" element={<Navigate to="/?tab=explore" replace />} />
          <Route path="/company/manage" element={<CompanyManage />} />
          {/* DEPRECATED: Community tab hidden from Explore; redirect old links to Passports. */}
          <Route path="/community" element={<Navigate to="/?tab=explore" replace />} />
          <Route path="/community/talk/:talkId" element={<CommunityTalkRedirect />} />
          <Route path="/admin/creators" element={<AdminGuard><CreatorManagement /></AdminGuard>} />
          <Route path="/admin/spotlights" element={<AdminGuard><SpotlightManager /></AdminGuard>} />
          <Route path="/admin/companies" element={<AdminGuard><CompanyManager /></AdminGuard>} />
          <Route path="/admin/b2b" element={<AdminGuard><B2BHub /></AdminGuard>} />
          <Route path="/admin/job-watcher" element={<AdminGuard><JobWatcherHub /></AdminGuard>} />
          {/* Recruiter Interview Hub — open to all signed-in recruiters,
              actual feature access gated inside the page (pilot mode). */}
          <Route path="/interviews" element={<RecruiterGuard><InterviewHub /></RecruiterGuard>} />
          <Route path="/interviews/:sessionId" element={<RecruiterGuard><InterviewReview /></RecruiterGuard>} />
          {/* Legacy admin-only paths — keep as redirects so existing links/bookmarks
              don't 404. The page itself enforces pilot access. */}
          <Route path="/admin/interviews" element={<Navigate to="/interviews" replace />} />
          <Route path="/admin/interviews/:sessionId" element={<LegacyInterviewSessionRedirect />} />
          <Route path="/interview/invite/:token" element={<InviteLanding />} />
          <Route path="/interview/room/:sessionId" element={<InterviewRoom />} />
          {/* Consumer-side AI Mock Interviews (Sprint A: admin-guarded) */}
          <Route path="/mock" element={<MockHome />} />
          <Route path="/mock/history" element={<MockHistory />} />
          <Route path="/mock/sessions/:sessionId" element={<MockReview />} />
          <Route path="/recruiter/prescreen/:candidateId/:sessionId" element={<RecruiterGuard><RecruiterPreScreenReview /></RecruiterGuard>} />
          <Route path="/spotlights" element={<Spotlights />} />
          {/* Passport content preview (Phase 1b) — standalone build/test route */}
          <Route path="/passport-preview" element={<PassportPreview />} />
          <Route path="/hiring-board" element={<Navigate to="/?tab=hire" replace />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/applications" element={<RecruiterGuard loadingIcon={ClipboardList}><Applications /></RecruiterGuard>} />
          {/* Public Lite-ATS career link — no auth, applicants are not logged in */}
          <Route path="/apply/:companyId/:jobRef" element={<ApplyPublic />} />
          {/* Passport share deep-link — recruiter login required (guarded inside the page) */}
          <Route path="/p/:token" element={<PassportShareLanding />} />
          {/* Dedicated page: only "interested" candidates */}
          <Route path="/interested" element={<InterestedPage />} />
          {/* Main recruiter candidate search (InterestedFeed.tsx) - search, discover, saved */}
          <Route path="/candidate-search" element={<InterestedFeed />} />
          {/* Add more routes here as we build them */}
          <Route path="/features" element={<div className="py-20 text-center"><h1>Features Page</h1></div>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/checkout" element={<StripeCheckout />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/login/*" element={<Login />} />
          <Route path="/signup-choice" element={<SignupChoice />} />
          <Route path="/signup/*" element={<Signup />} />
          <Route path="/recruiter-signup/*" element={<RecruiterSignup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/email-confirmation" element={<EmailConfirmation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/resend-confirmation" element={<ResendConfirmation />} />
          {/* Legal */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/imprint" element={<Imprint />} />
          <Route path="/legal/avv" element={<Avv />} />
          <Route path="/legal/dpa" element={<Avv />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/creator-agreement" element={<CreatorAgreement />} />
          <Route path="/help/ashby" element={<AshbyIntegrationHelp />} />
          </Routes>
          </Layout>
          </ViewModeProvider>
        </UserProfileProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
